import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { KeyRound, Handshake } from "lucide-react";
import { PRICE } from "@/lib/api";
import { Logo } from "@/components/Logo";
import CheckoutButton from "@/components/landing/CheckoutButton";
import { MuestraTaller } from "@/components/landing/Product";

const HERO_IMG = "https://images.unsplash.com/photo-1740679954227-a0cd19c042a6?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";

export const NavBar = () => (
  <header className="px-5 sm:px-8 pt-5">
    <div className="max-w-6xl mx-auto flex items-center justify-between">
      <Link to="/" data-testid="nav-logo" className="text-xl sm:text-2xl font-semibold text-[var(--ink)]"><Logo /></Link>
      <nav aria-label="Navegación principal" className="flex items-center gap-3 sm:gap-6">
        <Link to="/colabora" aria-label="Colabora con nosotros" title="Colabora con nosotros" className="text-xs sm:text-sm font-semibold hover:underline underline-offset-4 whitespace-nowrap inline-flex items-center gap-1">
          <Handshake size={16} className="sm:hidden" /> <span className="sm:hidden">Colabora</span><span className="hidden sm:inline">Colabora con nosotros</span>
        </Link>
        <Link to="/acceder" data-testid="nav-access-link" className="text-sm font-semibold inline-flex items-center gap-1.5 hover:underline underline-offset-4 whitespace-nowrap">
          <KeyRound size={16} /> <span className="sm:hidden">Mi pack</span><span className="hidden sm:inline">Acceder a mi pack</span>
        </Link>
      </nav>
    </div>
  </header>
);

export const Hero = () => {
  return (
  <section data-testid="hero-section" className="px-5 sm:px-8 pt-8 sm:pt-16 pb-16 sm:pb-20">
    <div className="max-w-6xl mx-auto grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <p data-testid="brand-tagline" className="hidden sm:block text-sm sm:text-base font-semibold text-[var(--sage-deep)] max-w-xl">En Dos Mundos. Psicoeducación para las relaciones, la familia y la crianza.</p>
        <p className="sm:mt-6 text-xs sm:text-sm font-semibold uppercase text-[#7a6c5e]">Taller destacado · Equipo en Calma · 21 días</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.08] max-w-2xl">
          Volved a hacer equipo en la crianza.
        </h1>
        <p className="mt-4 sm:mt-6 text-base sm:text-lg max-w-xl text-[#5a4f46]">
          Criar en equipo no siempre es fácil. Entre las prisas, el cansancio y las diferentes formas de educar, a veces las conversaciones terminan en discusiones y cuesta encontrar la manera de entenderse.
        </p>
        <p className="mt-3 text-base sm:text-lg max-w-xl text-[#5a4f46]">
          Equipo en Calma es un recorrido psicoeducativo online para parejas con hijos e hijas de 0 a 5 años. Encontraréis herramientas para mejorar la comunicación, afrontar los desacuerdos y construir una crianza más consciente y compartida.
        </p>
        <p className="mt-3 text-base sm:text-lg font-semibold max-w-xl text-[var(--forest)]">
          21 días de actividades prácticas · unos 10 minutos al día · acompañamiento psicoeducativo por WhatsApp durante el taller.
        </p>
        <p className="mt-2 text-base sm:text-lg max-w-xl text-[#5a4f46]">
          Un espacio para aprender, reflexionar y volver a hacer equipo, a vuestro ritmo y sin horarios ni videollamadas.
        </p>
        <div className="mt-6 flex flex-col items-start gap-3">
          <CheckoutButton testId="hero-cta-button" className="btn-accent text-lg w-full sm:w-auto">Quiero empezar ya · {PRICE} €</CheckoutButton>
          <MuestraTaller />
          <p className="text-sm text-[#5a4f46] max-w-md">Pago único de {PRICE} €. Incluye cinco planificadores imprimibles.</p>
        </div>
      </motion.div>
      <motion.div className="relative" initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }}>
        <div className="absolute -top-4 -left-4 w-full h-full rounded-[2.5rem] bg-[var(--terra)] rotate-[-3deg]" />
        <img src={HERO_IMG} alt="Familia compartiendo un momento en casa" loading="eager" fetchPriority="high" className="relative w-full aspect-[4/5] object-cover rounded-[2.5rem]" />
        <div className="absolute -bottom-6 right-4 sm:-right-6 bg-white rounded-2xl px-5 py-4 shadow-xl rotate-[2deg]" data-testid="hero-badge">
          <p className="font-hand text-2xl text-[var(--sage-deep)] leading-none">Dudas por WhatsApp</p>
          <p className="text-sm font-semibold">Te acompañamos durante todo el reto</p>
        </div>
      </motion.div>
    </div>
  </section>
  );
};
